import React from "react";
import { useTranslation } from "react-i18next";
import { DollarSign, Gift, AlertCircle, Percent } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "../../ui";
import { ShadcnInput, ShadcnButton } from "../../ui";
import { Checkbox } from "../../ui/checkbox";
import { PriceInput } from "../../form";
import { formatCurrency, formatPercentageInput } from "../../../utils/formatters";
import { cn } from "../../../utils/cn";
import { Vehicle, Benefit, FinancialCharge } from "../../../types";

interface FinancialProposalSectionProps {
 selectedVehicle: Vehicle | null;
 vehicleValueFormatted: string;
 onVehicleValueChange: (value: string) => void;
 onApplyAdvertisedVehicleValue: () => void;
 priceInputType: "value" | "discount";
 onPriceInputTypeChange: (type: "value" | "discount") => void;
 proposedPriceFormatted: string;
 onProposedPriceChange: (value: string) => void;
 discountInputFormatted: string;
 onDiscountInputChange: (value: string) => void;
 priceError: string;
 isPriceInvalid: () => boolean;
 proposedPrice: number;
 discount: number;
 discountPercentage: number;
 benefits: Benefit[];
 selectedBenefits: string[];
 onBenefitsChange: (benefitIds: string[]) => void;
 benefitsWithFullDiscount: string[];
 onBenefitsFullDiscountChange: (benefitIds: string[]) => void;
 charges: FinancialCharge[];
 selectedCharges: string[];
 onChargesChange: (chargeIds: string[]) => void;
 chargeDiscounts: Record<string, number>;
 onChargeDiscountChange: (chargeId: string, discount: number) => void;
 chargesWithDiscountEnabled: string[];
 onChargesDiscountEnabledChange: (chargeIds: string[]) => void;
 getVehiclePrice: (vehicle: Vehicle) => number;
 getChargeOriginalValue: (charge: FinancialCharge) => number;
 getChargeValue: (charge: FinancialCharge) => number;
 calculateBenefitsTotal: () => number;
 calculateChargesTotal: () => number;
}

export const FinancialProposalSection: React.FC<FinancialProposalSectionProps> = ({
 selectedVehicle,
 vehicleValueFormatted,
 onVehicleValueChange,
 onApplyAdvertisedVehicleValue,
 priceInputType,
 onPriceInputTypeChange,
 proposedPriceFormatted,
 onProposedPriceChange,
 discountInputFormatted,
 onDiscountInputChange,
 priceError,
 isPriceInvalid,
 proposedPrice,
 discount,
 discountPercentage,
 benefits,
 selectedBenefits,
 onBenefitsChange,
 benefitsWithFullDiscount,
 onBenefitsFullDiscountChange,
 charges,
 selectedCharges,
 onChargesChange,
 chargeDiscounts,
 onChargeDiscountChange,
 chargesWithDiscountEnabled,
 onChargesDiscountEnabledChange,
 getVehiclePrice,
 getChargeOriginalValue,
 getChargeValue,
 calculateBenefitsTotal,
 calculateChargesTotal,
}) => {
 const { t } = useTranslation();
 const advertisedAmount = selectedVehicle ? getVehiclePrice(selectedVehicle) : 0;

 const handleBenefitToggle = (benefitId: string, checked: boolean) => {
 if (checked) {
 onBenefitsChange([...selectedBenefits, benefitId]);
 } else {
 onBenefitsChange(selectedBenefits.filter((id) => id !== benefitId));
 onBenefitsFullDiscountChange(
 benefitsWithFullDiscount.filter((id) => id !== benefitId)
 );
 }
 };

 const handleBenefitFullDiscountToggle = (benefitId: string, checked: boolean) => {
 if (checked) {
 onBenefitsFullDiscountChange([...benefitsWithFullDiscount, benefitId]);
 } else {
 onBenefitsFullDiscountChange(
 benefitsWithFullDiscount.filter((id) => id !== benefitId)
 );
 }
 };

 const handleChargeToggle = (chargeId: string, checked: boolean) => {
 const charge = charges.find((c) => c.id === chargeId);
 if (charge?.isRequired && !checked) {
 return;
 }

 if (checked) {
 onChargesChange([...selectedCharges, chargeId]);
 } else {
 onChargesChange(selectedCharges.filter((id) => id !== chargeId));
 onChargeDiscountChange(chargeId, 0);
 onChargesDiscountEnabledChange(
 chargesWithDiscountEnabled.filter((id) => id !== chargeId)
 );
 }
 };

 const handleChargeDiscountEnabledToggle = (chargeId: string, checked: boolean) => {
 if (checked) {
 onChargesDiscountEnabledChange([...chargesWithDiscountEnabled, chargeId]);
 } else {
 onChargesDiscountEnabledChange(
 chargesWithDiscountEnabled.filter((id) => id !== chargeId)
 );
 onChargeDiscountChange(chargeId, 0);
 }
 };

 const handleChargeDiscountInputChange = (chargeId: string, value: string) => {
 const cleanValue = value.replace(/[^\d,.]/g, "").replace(",", ".");
 const parts = cleanValue.split(".");
 const finalValue =
 parts.length > 2
 ? parts[0] + "." + parts.slice(1).join("")
 : cleanValue;
 const numericValue = parseFloat(finalValue) || 0;
 const clampedValue = Math.min(Math.max(numericValue, 0), 100);
 onChargeDiscountChange(chargeId, clampedValue);
 };

 const sectionLabel = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

 return (
 <Card className="overflow-hidden border-border shadow-sm">
 <CardHeader className="border-b border-border bg-muted/20 pb-4">
 <CardTitle className="flex items-center gap-2 text-lg font-display">
 <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
 <DollarSign className="h-5 w-5" />
 </span>
 {t("proposals.form.financialProposalTitle")}
 </CardTitle>
 <p className="text-sm text-muted-foreground font-normal mt-1 pl-11">
 {t("proposals.form.financialIntro")}
 </p>
 </CardHeader>
 <CardContent className="space-y-8 pt-6">
 <div>
 <p className={cn(sectionLabel, "mb-3")}>
 {t("proposals.form.referenceSectionTitle")}
 </p>
 <div
 className={cn(
 "rounded-xl border border-border bg-card p-4 sm:p-5",
 "border-l-[3px] border-l-accent/70 shadow-sm"
 )}
 >
 <label className="text-sm font-medium text-foreground block mb-2">
 {t("proposals.form.advertisedPrice")}
 </label>
 <ShadcnInput
 value={
 selectedVehicle && advertisedAmount > 0
 ? formatCurrency(advertisedAmount)
 : "R$ 0,00"
 }
 disabled
 readOnly
 className="bg-muted/30 font-medium tabular-nums"
 />
 {!selectedVehicle && (
 <p className="mt-2 text-xs text-muted-foreground">
 {t("proposals.form.selectVehicleForPrices")}
 </p>
 )}
 </div>
 </div>

 <div>
 <p className={cn(sectionLabel, "mb-3")}>
 {t("proposals.form.negotiationSectionTitle")}
 </p>
 <div
 className={cn(
 "rounded-xl border border-border bg-muted/20 p-4 sm:p-5 space-y-5",
 "ring-1 ring-border/50"
 )}
 >
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
 <div className="space-y-2 lg:col-span-2">
 <label className="text-sm font-medium text-foreground">
 {t("proposals.form.negotiatedVehicleValue")}
 </label>
 <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
 <PriceInput
 value={vehicleValueFormatted}
 onChange={onVehicleValueChange}
 placeholder="R$ 0,00"
 className="bg-card flex-1"
 />
 <ShadcnButton
 type="button"
 variant="secondary"
 onClick={onApplyAdvertisedVehicleValue}
 disabled={!selectedVehicle || advertisedAmount <= 0}
 className="shrink-0 w-full sm:w-auto"
 >
 {t("proposals.form.applyVehicleAdvertisedPrice")}
 </ShadcnButton>
 </div>
 </div>

 <div className="space-y-2 lg:col-span-2">
 <label className="text-sm font-medium text-foreground flex items-center gap-2">
 <Percent className="h-4 w-4 text-muted-foreground" />
 Como informar o preço proposto
 </label>
 <div className="grid grid-cols-2 gap-2">
 <ShadcnButton
 type="button"
 variant={priceInputType === "value" ? "default" : "outline"}
 onClick={() => {
 onPriceInputTypeChange("value");
 onDiscountInputChange("");
 }}
 className="w-full"
 >
 Valor em R$
 </ShadcnButton>
 <ShadcnButton
 type="button"
 variant={priceInputType === "discount" ? "default" : "outline"}
 onClick={() => {
 onPriceInputTypeChange("discount");
 if (selectedVehicle && proposedPrice > 0) {
 const originalPrice = getVehiclePrice(selectedVehicle);
 if (originalPrice > 0) {
 onDiscountInputChange(formatPercentageInput(discountPercentage));
 }
 }
 }}
 className="w-full"
 >
 Desconto %
 </ShadcnButton>
 </div>
 </div>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 pt-1 border-t border-border/80">
 {priceInputType === "value" ? (
 <div className="space-y-2 lg:col-span-1">
 <label
 className={cn(
 "text-sm font-medium mb-0",
 isPriceInvalid() ? "text-red-600 dark:text-red-400" : "text-foreground"
 )}
 >
 Preço proposto *
 </label>
 <PriceInput
 value={proposedPriceFormatted}
 onChange={onProposedPriceChange}
 placeholder="R$ 0,00"
 className={cn(
 "bg-card",
 isPriceInvalid() &&
 "border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500"
 )}
 />
 {priceError && (
 <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
 <AlertCircle className="h-4 w-4 shrink-0" />
 {priceError}
 </p>
 )}
 </div>
 ) : (
 <div className="space-y-2 lg:col-span-1">
 <label
 className={cn(
 "text-sm font-medium mb-0",
 isPriceInvalid() ? "text-red-600 dark:text-red-400" : "text-foreground"
 )}
 >
 Desconto (%) *
 </label>
 <ShadcnInput
 type="text"
 value={discountInputFormatted}
 onChange={(e) => onDiscountInputChange(e.target.value)}
 placeholder="0%"
 className={cn(
 "bg-card",
 isPriceInvalid() &&
 "border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500"
 )}
 />
 {priceError && (
 <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
 <AlertCircle className="h-4 w-4 shrink-0" />
 {priceError}
 </p>
 )}
 {!priceError && proposedPrice > 0 && (
 <p className="text-sm text-muted-foreground">
 Preço calculado:{" "}
 <span className="font-semibold text-foreground">
 {formatCurrency(proposedPrice)}
 </span>
 </p>
 )}
 </div>
 )}

 {priceInputType === "value" && proposedPrice > 0 && (
 <div className="space-y-2 rounded-lg border border-border bg-card p-4 lg:col-span-1">
 <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
 Resumo do desconto
 </label>
 <ShadcnInput
 value={formatCurrency(discount)}
 disabled
 readOnly
 className="bg-muted/30 font-medium tabular-nums"
 />
 <p className="text-sm text-muted-foreground">
 {discountPercentage.toFixed(1)}% em relação ao preço original
 </p>
 </div>
 )}
 </div>
 </div>
 </div>

 <div>
 <p className={cn(sectionLabel, "mb-3")}>Benefícios</p>
 {benefits.length === 0 ? (
 <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border px-4 py-6 text-center bg-muted/10">
 Nenhum benefício disponível
 </p>
 ) : (
 <div className="rounded-xl border border-border bg-muted/15 overflow-hidden">
 <div className="max-h-52 overflow-y-auto p-2 space-y-1">
 {benefits.map((benefit) => (
 <div
 key={benefit.id}
 className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg hover:bg-card/80 transition-colors"
 >
 <div className="flex items-center gap-3 flex-1 min-w-0">
 <Checkbox
 checked={selectedBenefits.includes(benefit.id)}
 onCheckedChange={(checked) =>
 handleBenefitToggle(benefit.id, checked as boolean)
 }
 />
 <Gift className="h-4 w-4 text-accent shrink-0" />
 <span className="text-sm text-foreground truncate">{benefit.name}</span>
 <span className="text-sm font-semibold text-foreground tabular-nums ml-auto sm:ml-0">
 {formatCurrency(benefit.value)}
 </span>
 </div>
 {selectedBenefits.includes(benefit.id) && (
 <div className="flex items-center gap-2 pl-9 sm:pl-0">
 <Checkbox
 checked={benefitsWithFullDiscount.includes(benefit.id)}
 onCheckedChange={(checked) =>
 handleBenefitFullDiscountToggle(benefit.id, checked as boolean)
 }
 />
 <span className="text-xs text-muted-foreground">Isenção total</span>
 </div>
 )}
 </div>
 ))}
 </div>
 </div>
 )}
 {selectedBenefits.length > 0 && (
 <p className="mt-3 text-sm text-muted-foreground">
 Total de benefícios:{" "}
 <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
 {formatCurrency(calculateBenefitsTotal())}
 </span>
 </p>
 )}
 </div>

 <div>
 <p className={cn(sectionLabel, "mb-3")}>Encargos financeiros</p>
 {charges.filter((c) => c.isActive).length === 0 ? (
 <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border px-4 py-6 text-center bg-muted/10">
 Nenhum encargo disponível
 </p>
 ) : (
 <div className="rounded-xl border border-border bg-muted/15 overflow-hidden">
 <div className="max-h-96 overflow-y-auto p-2 space-y-1">
 {charges
 .filter((c) => c.isActive)
 .map((charge) => {
 const isSelected = selectedCharges.includes(charge.id);
 const chargeDiscount = chargeDiscounts[charge.id] || 0;

 return (
 <div
 key={charge.id}
 className="space-y-2 p-3 rounded-lg hover:bg-card/80 transition-colors border-b border-border/40 last:border-0"
 >
 <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
 <div className="flex items-start gap-3 flex-1 min-w-0">
 <Checkbox
 checked={isSelected}
 disabled={charge.isRequired}
 onCheckedChange={(checked) =>
 handleChargeToggle(charge.id, checked as boolean)
 }
 className="mt-0.5"
 />
 <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
 <div className="flex-1 min-w-0">
 <span className="text-sm font-medium text-foreground">
 {charge.name}
 {charge.isRequired && (
 <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">
 Obrigatório
 </span>
 )}
 </span>
 </div>
 </div>
 <div className="flex flex-col items-end gap-0.5 pl-9 sm:pl-0 shrink-0">
 {charge.isPercentage ? (
 <>
 <span className="text-sm font-semibold text-foreground tabular-nums">
 {charge.percentage}%
 </span>
 {selectedVehicle && (
 <span className="text-xs text-muted-foreground text-right">
 {formatCurrency(getChargeOriginalValue(charge))}
 {chargeDiscount > 0 && (
 <span className="text-emerald-600 dark:text-emerald-400 ml-1">
 → {formatCurrency(getChargeValue(charge))}
 </span>
 )}
 </span>
 )}
 </>
 ) : (
 <>
 <span className="text-sm font-semibold text-foreground tabular-nums">
 {formatCurrency(getChargeOriginalValue(charge))}
 </span>
 {chargeDiscount > 0 && (
 <span className="text-xs text-emerald-600 dark:text-emerald-400">
 → {formatCurrency(getChargeValue(charge))}
 </span>
 )}
 </>
 )}
 </div>
 </div>

 {isSelected && (
 <div className="ml-0 sm:ml-9 space-y-2 sm:border-l-2 sm:border-accent/20 sm:pl-3">
 <div className="flex items-center gap-2">
 <Checkbox
 checked={chargesWithDiscountEnabled.includes(charge.id)}
 onCheckedChange={(checked) =>
 handleChargeDiscountEnabledToggle(charge.id, checked as boolean)
 }
 />
 <span className="text-xs text-muted-foreground">Aplicar desconto no encargo</span>
 </div>

 {chargesWithDiscountEnabled.includes(charge.id) && (
 <div className="flex flex-wrap items-center gap-2">
 <span className="text-xs text-muted-foreground whitespace-nowrap">
 Percentual:
 </span>
 <div className="flex items-center gap-2 flex-1 min-w-[8rem] max-w-xs">
 <ShadcnInput
 type="text"
 value={chargeDiscount > 0 ? `${chargeDiscount}%` : ""}
 onChange={(e) =>
 handleChargeDiscountInputChange(charge.id, e.target.value)
 }
 placeholder="0%"
 className="text-xs py-1 h-8 bg-card"
 />
 </div>
 </div>
 )}
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 )}
 {selectedCharges.length > 0 && (
 <p className="mt-3 text-sm text-muted-foreground">
 Total de encargos:{" "}
 <span className="font-semibold text-red-600 dark:text-red-400 tabular-nums">
 {formatCurrency(calculateChargesTotal())}
 </span>
 </p>
 )}
 </div>
 </CardContent>
 </Card>
 );
};
